import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { Leaf, Droplets, Sun, Thermometer, TrendingUp, Pencil, Trash2, Clock, Tag, Layers, Eye, Camera, Archive, Link2 as LinkIcon, Loader2 } from 'lucide-react';
import { getPhotoThumbnail } from '../services/localPlantStore';
import { capitalizeTemperatureUnits } from './add-plant-steps/plantUtils';
import { useLanguage } from '../contexts/LanguageContext';
import { MonsteraSpinner } from './MonsteraSpinner';
import { DormancyTimeline } from './DormancyTimeline';

import { API_BASE_URL } from '../utils/apiBase';
const BACKEND_URL = API_BASE_URL;

// Persistent thumbnail cache — survives component unmount/remount (pagination, re-render)
const _thumbCache = new Map();

/**
 * Drop the cached thumbnail(s) for a plant so the next render of any
 * `PlantCard` for it re-reads from IDB / re-fetches from Drive. Call
 * this whenever a photo mutation happens (add/delete/replace/promote
 * cover) — otherwise the library-grid card keeps rendering the old
 * image until a full page reload.
 */
export function invalidatePlantCardThumb(plantIdOrDriveId) {
  if (!plantIdOrDriveId) return;
  _thumbCache.delete(plantIdOrDriveId);
}

// Style helpers extracted from nested ternaries for clarity.
const getCardBorder = ({ bulkSelected, incomplete }) => {
  if (bulkSelected) return '2px solid hsl(143, 28%, 45%)';
  if (incomplete) return '2px solid hsl(21, 70%, 75%)';
  return '1px solid hsl(143, 28%, 90%)';
};

const getPhotoCount = (plant) => {
  if (plant.photo_drive_ids?.length) return plant.photo_drive_ids.length;
  if (plant.photo_drive_id) return 1;
  if (plant.has_photo) return 1;
  return 0;
};

export const PlantCard = ({ plant, onClick, onEdit, onDelete, onArchive, onCopyLink, isLocalMode = false, compact = false, listView = false, bulkMode = false, bulkSelected = false, onBulkToggle, addedDateLabel = null }) => {
  const { t } = useLanguage();
  const [copyingLink, setCopyingLink] = useState(false);
  const [photoUrl, setPhotoUrl] = useState(() => {
    const key = isLocalMode ? plant.plant_id : plant.photo_drive_id;
    return key ? (_thumbCache.get(key) || null) : null;
  });
  const [loading, setLoading] = useState(false);
  const [imageLoaded, setImageLoaded] = useState(() => {
    const key = isLocalMode ? plant.plant_id : plant.photo_drive_id;
    return key ? _thumbCache.has(key) : false;
  });
  const [isVisible, setIsVisible] = useState(false);
  const cardRef = useRef(null);

  // Lazy load: only fetch photo when card scrolls into view
  useEffect(() => {
    const el = cardRef.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
          observer.disconnect();
        }
      },
      { rootMargin: '500px' }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    if (!isVisible) return;
    const photoKey = isLocalMode ? plant.plant_id : plant.photo_drive_id;
    if (!photoKey) return;
    // Already have it from cache
    if (_thumbCache.has(photoKey)) {
      setPhotoUrl(_thumbCache.get(photoKey));
      return;
    }
    let cancelled = false;
    const loadPhoto = async () => {
      try {
        setLoading(true);
        if (isLocalMode) {
          const thumb = await getPhotoThumbnail(plant.plant_id);
          if (!cancelled && thumb) {
            const url = `data:image/jpeg;base64,${thumb}`;
            _thumbCache.set(photoKey, url);
            setPhotoUrl(url);
          }
        } else {
          const sessionToken = localStorage.getItem('auth_hint');
          const response = await axios.get(
            `${BACKEND_URL}/api/photos/${plant.photo_drive_id}?thumbnail=true`,
            {
              headers: { Authorization: `Bearer ${sessionToken}` },
              responseType: 'blob'
            }
          );
          if (!cancelled) {
            const url = URL.createObjectURL(response.data);
            _thumbCache.set(photoKey, url);
            setPhotoUrl(url);
          }
        }
      } catch (error) {
        console.error('Failed to load photo:', error);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    loadPhoto();
    return () => { cancelled = true; };
  }, [isVisible, plant.photo_drive_id, plant.plant_id, isLocalMode]);

  // No revokeObjectURL on unmount — cache keeps URLs alive across pagination

  const handleEdit = (e) => {
    e.stopPropagation();
    onEdit(plant);
  };

  const handleDelete = (e) => {
    e.stopPropagation();
    onDelete(plant.plant_id);
  };

  const handleArchive = (e) => {
    e.stopPropagation();
    if (onArchive) onArchive(plant);
  };

  const handleCopyLink = async (e) => {
    e.stopPropagation();
    if (!onCopyLink || copyingLink) return;
    setCopyingLink(true);
    try { await onCopyLink(plant); } finally { setCopyingLink(false); }
  };

  if (listView) {
    return (
      <div
        ref={cardRef}
        onClick={bulkMode ? () => onBulkToggle?.(plant.plant_id) : onClick}
        onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); bulkMode ? onBulkToggle?.(plant.plant_id) : onClick(); } }}
        className="flex items-center gap-3 sm:gap-4 px-3 sm:px-4 py-3 bg-white cursor-pointer hover:bg-gray-50 transition-colors focus-visible:ring-2 focus-visible:ring-inset"
        style={{ borderBottom: '1px solid hsl(143, 28%, 93%)', '--tw-ring-color': 'hsl(143, 28%, 45%)', backgroundColor: bulkSelected ? 'hsl(143, 28%, 95%)' : undefined }}
        role="article" tabIndex={0}
        aria-label={`${plant.common_name || 'Unnamed plant'}${plant.incomplete ? ' (incomplete)' : ''}`}
        data-testid={`plant-card-${plant.plant_id}`}
      >
        {/* Bulk edit checkbox */}
        {bulkMode && (
          <div
            className="w-5 h-5 rounded border-2 flex items-center justify-center flex-shrink-0 transition-colors"
            style={{
              borderColor: bulkSelected ? 'hsl(143, 28%, 35%)' : 'hsl(143, 28%, 70%)',
              backgroundColor: bulkSelected ? 'hsl(143, 28%, 35%)' : 'transparent'
            }}
            data-testid={`bulk-checkbox-${plant.plant_id}`}
          >
            {bulkSelected && <span className="text-white text-xs font-bold">&#10003;</span>}
          </div>
        )}
        {/* Thumbnail */}
        <div className="relative w-10 h-10 sm:w-12 sm:h-12 rounded-lg overflow-hidden flex-shrink-0" style={{ backgroundColor: 'hsl(82, 55%, 95%)' }}>
          {loading ? (
            <div className="w-full h-full flex items-center justify-center">
              <MonsteraSpinner className="w-6 h-6" style={{ opacity: 0.4 }} />
            </div>
          ) : photoUrl ? (
            <>
              {!imageLoaded && (
                <div
                  className="absolute inset-0 animate-pulse"
                  aria-hidden="true"
                  style={{
                    background: 'linear-gradient(135deg, hsl(82,45%,86%), hsl(143,22%,82%))',
                    filter: 'blur(4px)',
                    transform: 'scale(1.1)',
                  }}
                />
              )}
              <img
                src={photoUrl}
                alt={plant.common_name}
                className="w-full h-full object-cover transition-opacity duration-300"
                style={{ opacity: imageLoaded ? 1 : 0 }}
                onLoad={() => setImageLoaded(true)}
              />
            </>
          ) : (
            <div className="w-full h-full flex items-center justify-center">
              <Leaf className="w-5 h-5" style={{ color: 'hsl(143, 28%, 25%)', opacity: 0.3 }} />
            </div>
          )}
        </div>
        {/* Name with labels */}
        <div className="flex-1 min-w-0">
          <p className="text-sm font-semibold truncate" style={{ color: 'hsl(143, 28%, 25%)' }}>
            <span className="hidden sm:inline text-xs font-normal mr-1" style={{ color: 'hsl(125, 12%, 50%)' }}>{t('commonName')}:</span>
            {plant.common_name}
          </p>
          {plant.scientific_name && (
            <p className="text-xs font-mono truncate" style={{ color: 'hsl(125, 12%, 42%)' }}>
              <span className="hidden sm:inline font-sans font-normal mr-1" style={{ color: 'hsl(125, 12%, 50%)' }}>{t('scientificName')}:</span>
              {plant.scientific_name}
            </p>
          )}
          {addedDateLabel && (
            <p className="text-xs mt-0.5" style={{ color: 'hsl(125, 12%, 50%)' }} data-testid={`plant-added-date-${plant.plant_id}`}>
              {t('addedOn') || 'Added'} {addedDateLabel}
            </p>
          )}
        </div>
        {plant.incomplete && (
          <span className="text-xs px-2 py-0.5 rounded-full flex-shrink-0 hidden sm:inline-flex" style={{ backgroundColor: 'hsl(21, 70%, 92%)', color: 'hsl(21, 70%, 45%)' }}>{t('incomplete')}</span>
        )}
        {/* Actions */}
        <div className="flex gap-1 sm:gap-1.5 flex-shrink-0">
          <button onClick={(e) => { e.stopPropagation(); onClick(); }} className="p-1.5 rounded-full hover:bg-gray-100 transition-colors" aria-label={`${t('viewDetails')} ${plant.common_name || 'plant'}`} title={t('viewDetails')} data-testid={`view-plant-${plant.plant_id}`}>
            <Eye className="w-3.5 h-3.5" style={{ color: 'hsl(143, 28%, 45%)' }} />
          </button>
          {onCopyLink && !plant.incomplete && (
            <button onClick={handleCopyLink} disabled={copyingLink} className="p-1.5 rounded-full hover:bg-gray-100 transition-colors disabled:opacity-60" aria-label={`${t('copyPlantLink') || 'Copy share link'} ${plant.common_name || 'plant'}`} title={t('copyPlantLink') || 'Copy share link'} data-testid={`copy-plant-link-${plant.plant_id}`}>
              {copyingLink ? <Loader2 className="w-3.5 h-3.5 animate-spin" style={{ color: 'hsl(143, 28%, 45%)' }} /> : <LinkIcon className="w-3.5 h-3.5" style={{ color: 'hsl(143, 28%, 45%)' }} />}
            </button>
          )}
          <button onClick={handleEdit} className="p-1.5 rounded-full hover:bg-gray-100 transition-colors" aria-label={`${t('editPlant')} ${plant.common_name || 'plant'}`} title={t('editPlant')} data-testid={`edit-plant-${plant.plant_id}`}>
            <Pencil className="w-3.5 h-3.5" style={{ color: 'hsl(143, 28%, 25%)' }} />
          </button>
          <button onClick={handleDelete} className="p-1.5 rounded-full hover:bg-gray-100 transition-colors" aria-label={`${t('delete')} ${plant.common_name || 'plant'}`} title={t('delete')} data-testid={`delete-plant-${plant.plant_id}`}>
            <Trash2 className="w-3.5 h-3.5" style={{ color: 'hsl(0, 70%, 50%)' }} />
          </button>
        </div>
      </div>
    );
  }

  return (
    <div
      ref={cardRef}
      onClick={bulkMode ? () => onBulkToggle?.(plant.plant_id) : onClick}
      onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); bulkMode ? onBulkToggle?.(plant.plant_id) : onClick(); } }}
      className="rounded-2xl overflow-hidden bg-white shadow-sm hover:shadow-xl transition-all duration-300 cursor-pointer group relative focus-visible:ring-2 focus-visible:ring-offset-2"
      style={{
        border: getCardBorder({ bulkSelected, incomplete: plant.incomplete }),
        '--tw-ring-color': 'hsl(143, 28%, 45%)'
      }}
      role="article"
      tabIndex={0}
      aria-label={`${plant.common_name || 'Unnamed plant'}${plant.incomplete ? ' (incomplete)' : ''}`}
      data-testid={`plant-card-${plant.plant_id}`}
    >
      {/* Bulk edit checkbox */}
      {bulkMode && (
        <div
          className="absolute top-3 right-3 z-10 w-6 h-6 rounded border-2 flex items-center justify-center transition-colors"
          style={{
            borderColor: bulkSelected ? 'hsl(143, 28%, 35%)' : 'white',
            backgroundColor: bulkSelected ? 'hsl(143, 28%, 35%)' : 'rgba(255,255,255,0.85)',
            boxShadow: '0 1px 3px rgba(0,0,0,0.2)'
          }}
          data-testid={`bulk-checkbox-${plant.plant_id}`}
        >
          {bulkSelected && <span className="text-white text-xs font-bold">&#10003;</span>}
        </div>
      )}
      {/* Incomplete badge */}
      {plant.incomplete && (
        <div className="absolute top-3 left-3 z-10 flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium"
          style={{ backgroundColor: 'hsl(21, 70%, 92%)', color: 'hsl(21, 70%, 45%)', border: '1px solid hsl(21, 70%, 80%)' }}
          data-testid={`incomplete-badge-${plant.plant_id}`}
        >
          <Clock className="w-3 h-3" />
          {t('incomplete')}
        </div>
      )}

      {/* Photo */}
      <div className={`relative overflow-hidden ${compact ? 'aspect-square' : 'aspect-[4/3]'}`} style={{ backgroundColor: 'hsl(82, 55%, 95%)' }}>
        {loading ? (
          <div className="absolute inset-0 flex items-center justify-center">
            <MonsteraSpinner className={compact ? 'w-10 h-10' : 'w-14 h-14'} style={{ opacity: 0.4 }} />
          </div>
        ) : photoUrl ? (
          <>
            {!imageLoaded && (
              <div
                className="absolute inset-0 animate-pulse"
                aria-hidden="true"
                style={{
                  background: 'linear-gradient(135deg, hsl(82,45%,86%) 0%, hsl(143,22%,82%) 50%, hsl(82,45%,88%) 100%)',
                  filter: 'blur(6px)',
                  transform: 'scale(1.1)',
                }}
              />
            )}
            <img
              src={photoUrl}
              alt={plant.common_name}
              className="w-full h-full object-cover group-hover:scale-105 transition-all duration-500"
              style={{ opacity: imageLoaded ? 1 : 0 }}
              onLoad={() => setImageLoaded(true)}
            />
          </>
        ) : (
          <div className="absolute inset-0 flex items-center justify-center">
            <Leaf className={compact ? 'w-8 h-8' : 'w-12 h-12'} style={{ color: 'hsl(143, 28%, 25%)', opacity: 0.3 }} />
          </div>
        )}
        <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity" />
        {/* Multi-photo count badge */}
        {(() => {
          const count = getPhotoCount(plant);
          return count > 1 ? (
            <div className="absolute top-3 right-3 z-10 flex items-center gap-1 px-1.5 py-0.5 rounded-full text-[10px] font-medium"
              style={{ backgroundColor: 'rgba(0,0,0,0.55)', color: 'white' }}
              data-testid={`photo-count-badge-${plant.plant_id}`}
            >
              <Camera className="w-3 h-3" /> {count}
            </div>
          ) : null;
        })()}
      </div>

      {/* Content - explicitly clickable to open detail */}
      <div className={compact ? 'p-3 pb-10' : 'p-5 pb-14'} onClick={(e) => { e.stopPropagation(); if (!bulkMode) onClick(); else onBulkToggle?.(plant.plant_id); }}>
        <h2 className={`font-display font-bold mb-1 ${compact ? 'text-sm' : 'text-xl'}`} style={{ color: 'hsl(143, 28%, 25%)' }}>
          {plant.common_name}
        </h2>
        {plant.scientific_name && !compact && (
          <p className="text-sm font-mono mb-4" style={{ color: 'hsl(125, 12%, 32%)' }}>
            {plant.scientific_name}
          </p>
        )}
        {addedDateLabel && (
          <p className={`text-xs ${compact ? 'mb-1' : 'mb-3'}`} style={{ color: 'hsl(125, 12%, 50%)' }} data-testid={`plant-added-date-${plant.plant_id}`}>
            {t('addedOn') || 'Added'} {addedDateLabel}
          </p>
        )}

        {/* Care Details — stacked rows */}
        {plant.care_details && !compact && (
          <div className="flex flex-col gap-1.5">
            {plant.care_details.family && (
              <div className="flex items-center gap-1.5 px-2 py-1 rounded-lg text-xs" style={{ backgroundColor: 'hsl(82, 55%, 95%)', color: 'hsl(125, 12%, 25%)' }}>
                <Tag className="w-3.5 h-3.5 flex-shrink-0" style={{ color: 'hsl(21, 70%, 57%)' }} /><span>{t('family')}: {plant.care_details.family}</span>
              </div>
            )}
            {plant.care_details.plant_type && (
              <div className="flex items-center gap-1.5 px-2 py-1 rounded-lg text-xs" style={{ backgroundColor: 'hsl(82, 55%, 95%)', color: 'hsl(125, 12%, 25%)' }}>
                <Layers className="w-3.5 h-3.5 flex-shrink-0" style={{ color: 'hsl(21, 70%, 57%)' }} /><span>{t('plantType')}: {plant.care_details.plant_type}</span>
              </div>
            )}
            {plant.care_details.watering && (
              <div className="flex items-center gap-1.5 px-2 py-1 rounded-lg text-xs" style={{ backgroundColor: 'hsl(82, 55%, 95%)', color: 'hsl(125, 12%, 25%)' }}>
                <Droplets className="w-3.5 h-3.5 flex-shrink-0" style={{ color: 'hsl(21, 70%, 57%)' }} /><span>{t('watering')}: {plant.care_details.watering}</span>
              </div>
            )}
            {plant.care_details.light && (
              <div className="flex items-center gap-1.5 px-2 py-1 rounded-lg text-xs" style={{ backgroundColor: 'hsl(82, 55%, 95%)', color: 'hsl(125, 12%, 25%)' }}>
                <Sun className="w-3.5 h-3.5 flex-shrink-0" style={{ color: 'hsl(21, 70%, 57%)' }} /><span>{t('light')}: {plant.care_details.light}</span>
              </div>
            )}
            {plant.care_details.humidity && (
              <div className="flex items-center gap-1.5 px-2 py-1 rounded-lg text-xs" style={{ backgroundColor: 'hsl(82, 55%, 95%)', color: 'hsl(125, 12%, 25%)' }}>
                <Leaf className="w-3.5 h-3.5 flex-shrink-0" style={{ color: 'hsl(21, 70%, 57%)' }} /><span>{t('humidity')}: {plant.care_details.humidity}</span>
              </div>
            )}
            {plant.care_details.temperature && (
              <div className="flex items-center gap-1.5 px-2 py-1 rounded-lg text-xs" style={{ backgroundColor: 'hsl(82, 55%, 95%)', color: 'hsl(125, 12%, 25%)' }}>
                <Thermometer className="w-3.5 h-3.5 flex-shrink-0" style={{ color: 'hsl(21, 70%, 57%)' }} /><span>{t('temperature')}: {capitalizeTemperatureUnits(plant.care_details.temperature)}</span>
              </div>
            )}
            {plant.care_details.toxicity && (
              <div className="flex items-center gap-1.5 px-2 py-1 rounded-lg text-xs" style={{ backgroundColor: 'hsl(82, 55%, 95%)', color: 'hsl(125, 12%, 25%)' }}>
                <Leaf className="w-3.5 h-3.5 flex-shrink-0" style={{ color: 'hsl(21, 70%, 57%)' }} /><span>{t('toxicity')}: {plant.care_details.toxicity}</span>
              </div>
            )}
            {plant.care_details.growth_rate && (
              <div className="flex items-center gap-1.5 px-2 py-1 rounded-lg text-xs" style={{ backgroundColor: 'hsl(82, 55%, 95%)', color: 'hsl(125, 12%, 25%)' }}>
                <TrendingUp className="w-3.5 h-3.5 flex-shrink-0" style={{ color: 'hsl(21, 70%, 57%)' }} /><span>{t('growthRate')}: {plant.care_details.growth_rate}</span>
              </div>
            )}
            <DormancyTimeline months={plant.care_details.dormancy_months} testid={`dormancy-timeline-${plant.plant_id}`} />
          </div>
        )}
      </div>

      {/* Edit, Archive, and Delete Icons - Bottom Right */}
      <div className={`absolute ${compact ? 'bottom-2 right-2' : 'bottom-3 right-3'} flex gap-2`}>
        {onCopyLink && !plant.incomplete && (
          <button
            onClick={handleCopyLink}
            disabled={copyingLink}
            className={`${compact ? 'p-1.5' : 'p-2'} rounded-full bg-white shadow-md hover:shadow-lg transition-all hover:scale-110 disabled:opacity-60`}
            style={{ border: '1px solid hsl(143, 28%, 85%)' }}
            aria-label={`${t('copyPlantLink') || 'Copy share link'} ${plant.common_name || 'plant'}`}
            title={t('copyPlantLink') || 'Copy share link'}
            data-testid={`copy-plant-link-${plant.plant_id}`}
          >
            {copyingLink
              ? <Loader2 className={`${compact ? 'w-3 h-3' : 'w-4 h-4'} animate-spin`} style={{ color: 'hsl(143, 28%, 45%)' }} />
              : <LinkIcon className={compact ? 'w-3 h-3' : 'w-4 h-4'} style={{ color: 'hsl(143, 28%, 45%)' }} />}
          </button>
        )}
        <button
          onClick={handleEdit}
          className={`${compact ? 'p-1.5' : 'p-2'} rounded-full bg-white shadow-md hover:shadow-lg transition-all hover:scale-110`}
          style={{ border: '1px solid hsl(143, 28%, 85%)' }}
          aria-label={`Edit ${plant.common_name || 'plant'}`}
          title={`Edit ${plant.common_name || 'plant'}`}
          data-testid={`edit-plant-${plant.plant_id}`}
        >
          <Pencil className={compact ? 'w-3 h-3' : 'w-4 h-4'} style={{ color: 'hsl(143, 28%, 25%)' }} />
        </button>
        {onArchive && (
          <button
            onClick={handleArchive}
            className={`${compact ? 'p-1.5' : 'p-2'} rounded-full bg-white shadow-md hover:shadow-lg transition-all hover:scale-110`}
            style={{ border: '1px solid hsl(40, 70%, 85%)' }}
            aria-label={`Archive ${plant.common_name || 'plant'}`}
            title={`Archive ${plant.common_name || 'plant'}`}
            data-testid={`archive-plant-${plant.plant_id}`}
          >
            <Archive className={compact ? 'w-3 h-3' : 'w-4 h-4'} style={{ color: 'hsl(40, 60%, 40%)' }} />
          </button>
        )}
        <button
          onClick={handleDelete}
          className={`${compact ? 'p-1.5' : 'p-2'} rounded-full bg-white shadow-md hover:shadow-lg transition-all hover:scale-110`}
          style={{ border: '1px solid hsl(0, 70%, 85%)' }}
          aria-label={`Delete ${plant.common_name || 'plant'}`}
          title={`Delete ${plant.common_name || 'plant'}`}
          data-testid={`delete-plant-${plant.plant_id}`}
        >
          <Trash2 className={compact ? 'w-3 h-3' : 'w-4 h-4'} style={{ color: 'hsl(0, 70%, 50%)' }} />
        </button>
      </div>
    </div>
  );
};
